import { useCallback, useEffect, useState } from 'react';

import { EventSourcePolyfill } from 'event-source-polyfill';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Answer } from './components/answer/answer';
import { LoadingPlan } from './components/processing/loading-plan';
import { Processing } from './components/processing/processing';
import { KimProgressMessageType } from '../../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useEventSource } from '@/api/hooks/useEventSource';
import { APIBaseChronos } from '@/api/hosts';
import { useDeleteThread } from '@/api/mutations/useDeleteThread';
import { useProcessPlan } from '@/api/mutations/useProcessPlan';
import { useGetMessages } from '@/api/queries/useGetMessages';
import { useGetThreadStatus } from '@/api/queries/useGetThreadStatus';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { sanitiseKimQuestion } from '@/helpers/sanitiseKimQuestion';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { KimMode, PlanStage, ThreadMessage, ThreadStatus, MessageType } from '@/types';

export function KimThread() {
  const { matterId, subsetId, threadId } = useParams();
  if (!matterId || !subsetId || !threadId) return;

  // Plan state
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planStage, setPlanStage] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<PlanStage[]>([]);
  const [planEditable, setPlanEditable] = useState(false);
  const [questionMode, setQuestionMode] = useState<KimMode | null>(null);

  // Execution
  const [planExecuting, setPlanExecuting] = useState(false);
  const [executionMessages, setExecutionMessages] = useState<string[]>([]);

  const [processedQuestion, setProcessedQuestion] = useState('');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const resetState = useCallback(() => {
    setLoadingPlan(false);
    setPlanStage({});
    setPlan([]);
    setPlanEditable(false);
    setExecutionMessages([]);
    setProcessedQuestion('');

    searchParams.delete('selectedDocId');
  }, [searchParams]);

  const handleError = useCallback(() => {
    toast.error('Error processing your question. Please try again.');
    resetState();
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim`);
  }, [resetState, navigate, matterId, subsetId]);

  const { eventSourceRef, setEventSourceRef, isMounted } = useEventSource();
  const { fetchConfigGET } = useGetFetchConfig();

  // Data fetching
  const {
    isLoading: isLoadingMessages,
    data: responseMessages,
    refetch: refetchMessages,
  } = useGetMessages(matterId, threadId);
  const { data: thread, refetch: refetchThreadStatus } = useGetThreadStatus({
    caseId: matterId,
    threadId,
  });

  // Mutations
  const { mutate: processPlan } = useProcessPlan();
  const { mutate: deleteThread } = useDeleteThread(matterId);

  // Execution State Helper Functions ----------------------------------------
  // Function for when plan is loading
  const pollForStatusChange = useCallback(async () => {
    const startTime = Date.now();

    return new Promise<void>((resolve) => {
      // Keep checking the thread status to see if it changes
      const pollInterval = setInterval(async () => {
        const response = await refetchThreadStatus();
        if (!response.data) return;
        const currentStatus: ThreadStatus = response.data?.status;

        if (currentStatus !== ThreadStatus.PlanLoading || Date.now() - startTime > 10000) {
          clearInterval(pollInterval);
          resolve();
        }
      }, 2000);
    });
  }, [refetchThreadStatus]);

  // Function for when plan is loaded
  const handlePlanLoaded = useCallback(
    (internalMessage: ThreadMessage, userMessage: ThreadMessage) => {
      if (internalMessage) {
        const plan = internalMessage.config?.plan;
        if (plan && Object.keys(plan).length > 0) {
          setPlan(plan);

          // Go through the plan and set the plan stage to not-started, unless the plan stage is already complete
          const updatedPlanStages = plan.reduce((acc: Record<string, string>, stage: PlanStage) => {
            acc[stage.name] = stage.status || ThreadStatus.NotStarted;
            return acc;
          }, {});
          setPlanStage(updatedPlanStages);
          setLoadingPlan(false);
        } else {
          console.error('Error loading plan');
          handleError();
          return;
        }
        // Save the mode and question from the messages
        setQuestionMode(internalMessage.config?.flowType ?? null);
        setProcessedQuestion(userMessage.content);
      }
    },
    [handleError],
  );

  const handleExecutionMonitoring = useCallback(
    (threadId: string, messageId: string) => {
      if (threadId && messageId) {
        const currentEventSource = eventSourceRef;
        if (currentEventSource?.url?.includes(`progress/${messageId}`)) {
          return;
        }

        const createEventSource = () => {
          let retryCount = 0;
          const maxRetries = 3;

          if (currentEventSource) {
            currentEventSource.close();
          }

          // --------------------SSE---------------------
          // Send the messageId to poll endpoint
          const eventSource = new EventSourcePolyfill(
            `${APIBaseChronos}/api/case/${matterId}/thread/progress/${messageId}`,
            {
              headers: Object.fromEntries(fetchConfigGET.headers.entries()),
              heartbeatTimeout: 360000,
            },
          );
          setEventSourceRef(eventSource);

          eventSource.onmessage = (event: any) => {
            // Only process messages if component is still mounted
            if (!isMounted.current) {
              eventSource.close();
              return;
            }

            const message: { stage: string; type: string; message: string } = JSON.parse(event.data);

            if (message.type === 'error') {
              console.error('Error from sqs queue');
              searchParams.delete('selectedDocId');
              handleError();
              eventSource.close();
              return;
            }

            // Start message
            if (message.type === KimProgressMessageType.TaskStart) {
              setPlanStage((prevStages) => ({
                ...prevStages,
                [message.stage]: 'in-progress',
              }));
              setExecutionMessages((prevMessages) => [...prevMessages, message.message]);
            }

            // Progress message
            if (message.type === 'task_message') {
              setExecutionMessages((prevMessages) => [...prevMessages, message.message]);
              return;
            }

            // Complete message
            if (message.type === KimProgressMessageType.TaskComplete) {
              setPlanStage((prevStages) => {
                const updatedStages = {
                  ...prevStages,
                  [message.stage]: 'complete',
                };
                return updatedStages;
              });
            }

            // If all stages are completed, then close the event source
            if (message.type === KimProgressMessageType.AllComplete) {
              setExecutionMessages((prevMessages) => [...prevMessages, 'Completing...']);
              eventSource.close();

              setTimeout(async () => {
                await refetchMessages();
                await refetchThreadStatus();
                resetState();
              }, 500);
            }
          };

          eventSource.onerror = (error: any) => {
            if (!isMounted.current) {
              eventSource.close();
              return;
            }

            eventSource.close();

            // Check if it's a network error and we haven't exceeded max retries
            if (error.error?.message === 'network error' && retryCount < maxRetries) {
              retryCount++;
              console.log(`Retrying connection... Attempt ${retryCount} of ${maxRetries}`);

              // Retry after a delay (exponential backoff)
              setTimeout(
                () => {
                  createEventSource();
                },
                Math.min(1000 * Math.pow(2, retryCount), 10000),
              );
            } else {
              // If max retries exceeded or different error, show error toast
              console.error('EventSource error:', error);
              handleError();
              searchParams.delete('selectedDocId');
              navigate(`${location.pathname}?${searchParams.toString()}`);
            }
          };
        };
        createEventSource();
      } else {
        console.error('Error with threadId/messageId', threadId, messageId);
        handleError();
      }
    },
    [
      fetchConfigGET.headers,
      setEventSourceRef,
      isMounted,
      searchParams,
      handleError,
      resetState,
      refetchMessages,
      navigate,
      location.pathname,
      eventSourceRef,
      matterId,
    ],
  );

  // Function for when plan is executing
  const handlePlanExecuting = useCallback(
    (internalMessage: ThreadMessage, userMessage: ThreadMessage, threadId: string, messageId: string) => {
      setPlanEditable(false);
      // First load the plan in
      handlePlanLoaded(internalMessage, userMessage);
      setPlanExecuting(true);
      // Then setup connection to monitor the execution messages
      handleExecutionMonitoring(threadId, messageId);
    },
    [handlePlanLoaded, handleExecutionMonitoring],
  );
  // ------------------------------------------------------------------------

  // When this component mounts, use the threadId to check the status
  useEffect(() => {
    if (!threadId || !thread) return;

    const initialiseThread = async () => {
      // Use the thread id to get the status of the thread
      const threadResponse = await refetchThreadStatus();
      if (!threadResponse.data) return;

      const currentStatus: ThreadStatus = threadResponse.data?.status;
      const activeMessageId = threadResponse.data?.activeMessageId;

      // Get any messages that are on the thread
      const messagesResponse = await refetchMessages();

      // Check message response is ok
      if (!messagesResponse.data) {
        handleError();
        return;
      }

      const internalMessage = messagesResponse.data.find((message) => message.type === MessageType.Internal);
      const userMessage = messagesResponse.data.find((message) => message.type === MessageType.User);

      // Depending on the thread status decide what to do
      switch (currentStatus) {
        // If the plan is loading then we just need to poll to wait until it is complete
        case ThreadStatus.PlanLoading:
          setLoadingPlan(true);
          await pollForStatusChange();
          break;

        // If the plan is loaded then we need to load the plan in
        case ThreadStatus.PlanLoaded:
          if (!internalMessage || !userMessage) {
            console.error('Error with internalMessage/userMessage in PlanLoaded');
            handleError();
            return;
          }
          setPlanEditable(true);
          handlePlanLoaded(internalMessage, userMessage);
          break;

        // If the plan is executing then we need to establish the SQS polling to check for updates
        case ThreadStatus.PlanExecuting:
          if (!internalMessage || !userMessage) {
            console.error('Error with internalMessage/userMessage in PlanExecuting');
            handleError();
            return;
          }
          handlePlanExecuting(internalMessage, userMessage, threadId, activeMessageId);
          break;

        // If the plan is complete then we need to just fetch all the messages on the thread to display  answer
        case ThreadStatus.PlanComplete:
          refetchMessages();
          break;
      }
    };

    initialiseThread();
  }, [threadId, thread?.status]);

  const executePlan = (currentPlan: PlanStage[]) => {
    setPlanExecuting(true);
    setExecutionMessages(['Starting plan execution...']);

    trackCustomEvent(TrackingEvents.KimExecutePlan, {
      threadId,
      matterId,
      questionMode,
    });

    processPlan(
      {
        threadId: threadId,
        caseId: matterId,
        question: sanitiseKimQuestion(processedQuestion || ''),
        mode: questionMode,
        plan: currentPlan,
      },
      {
        onSuccess: (data) => handleExecutionMonitoring(data.threadId, data.messageId),
        onError: (error) => {
          console.error('Error executing plan:', error);
          handleError();
        },
      },
    );
  };

  const cancelPlan = async () => {
    await deleteThread(threadId);
    resetState();
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim`);
  };

  const threadStatus = thread?.status;
  const completed = threadStatus === ThreadStatus.PlanComplete || threadStatus === ThreadStatus.Failed;
  const executing =
    threadStatus === ThreadStatus.PlanExecuting ||
    threadStatus === ThreadStatus.PlanLoading ||
    threadStatus === ThreadStatus.PlanLoaded;

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          { href: '/matters', label: 'Matters', active: false },
          { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
          { href: `/matters/${matterId}/subsets/${subsetId}/kim`, label: 'Kim', active: true },
        ]}
      />
      <div className="flex-1 overflow-hidden">
        {completed ? (
          <Answer
            caseId={matterId}
            threadId={threadId}
            messages={responseMessages}
            isLoadingMessages={isLoadingMessages}
          />
        ) : executing ? (
          <Processing
            planStage={planStage}
            plan={plan}
            setPlan={setPlan}
            planEditable={planEditable}
            executePlan={executePlan}
            loadingPlan={loadingPlan}
            loading={planExecuting}
            messages={executionMessages}
            cancelPlan={cancelPlan}
            processedQuestion={processedQuestion}
          />
        ) : threadStatus === ThreadStatus.NotStarted ? (
          <LoadingPlan />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="h-full w-56 min-w-56 border-r border-gray-200"></div>
            <div className="flex w-full flex-col items-center" />
          </div>
        )}
      </div>
    </div>
  );
}
